import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, LinearProgress, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material'
import { Container } from 'components'
import BusinessPage from '../../components/BusinessPage'
import { defaultPeriod, periodQuery, useReport, PeriodFilter, ReportState, Metric, MetricGrid, money } from '../../components/Reporting'
import Chart from './components/Chart'

const charts = {
  IMPRESSION: ['Impressions', 'Recorded offer impressions.'],
  CLAIM: ['Claims', 'Recorded claim debits, including card-funded and promotional claims.'],
  SPEND: ['Spend', 'USD spent from card-funded and promotional credit.'],
  TOTAL_CLICKS: ['Total clicks', 'Recorded homepage, favorite, phone, direction, claim, and T&C clicks.'],
  HOMEPAGE: ['Clicks - Homepage', 'Recorded homepage clicks, including clicks without a charge.'],
  FAVORITE: ['Clicks - Favorites', 'Recorded favorite clicks, including offers later removed from favorites.'],
  PHONE: ['Clicks - Phone', 'Recorded phone clicks.'],
  DIRECTIONS: ['Clicks - Directions', 'Recorded direction clicks.'],
  TERMS_AND_CONDITIONS: ['Clicks - T&C', 'Recorded offer details and terms views.'],
  BUDGET: ['Account balance', 'Combined card-funded and promotional credit in USD, as of each plotted time.'],
}
const overviewParams = {}
const activeOfferParams = { status: 'ACTIVE', size: 1, page: 0 }
const metricGridStyle = { gridAutoRows: '1fr', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' } }
const metricStyle = {
  display: 'flex', flexDirection: 'column',
  '& > p:first-of-type': { minHeight: '2.5em' },
}
function DashboardMetric(props) {
  return <Metric {...props} sx={metricStyle} />
}
export default function Dashboard() {
  const businessId = useSelector(state => state.business.businessId)
  const [period, setPeriod] = useState(defaultPeriod)
  const [chartType, setChartType] = useState('IMPRESSION')
  const dates = periodQuery(period)
  const overview = useReport(`/business/${businessId}/dashboard/overview`, overviewParams, Boolean(businessId))
  const activeOffers = useReport(`/offer/business/${businessId}`, activeOfferParams, Boolean(businessId))
  const widgets = useReport(`/business/${businessId}/widgets`, dates, Boolean(businessId))
  const chart = useReport(`/business/${businessId}/graph`, dates && { ...dates, type: chartType }, Boolean(businessId))
  const [label, description] = charts[chartType]
  return <BusinessPage title="Dashboard"><Container><Stack spacing={2.5} pb={3}>
    <Typography color="text.secondary">See how people discover and use your offers, and track the credit you spend.</Typography>
    <Box component="section" aria-labelledby="overview-heading">
      <Typography id="overview-heading" component="h2" variant="h6" mb={2}>Overall overview</Typography>
      <Stack spacing={1} mb={2}>
        {(activeOffers.loading || overview.loading) && <LinearProgress aria-label="Loading overview" />}
        {activeOffers.error && <ReportState report={activeOffers} label="Active offers" />}
        {overview.error && <ReportState report={overview} label="All-time metrics" />}
      </Stack>
      <MetricGrid sx={metricGridStyle}>
        <DashboardMetric label="Active offers" value={activeOffers.data?.totalElements?.toLocaleString('en-US')} />
        <DashboardMetric label="All-time clicks" value={overview.data?.allTimeClicks} />
        <DashboardMetric label="All-time claims" value={overview.data?.allTimeClaims} />
      </MetricGrid>
    </Box>
    <Box component="section" aria-labelledby="period-heading">
      <Stack spacing={2}>
        <Box>
          <Typography id="period-heading" component="h2" variant="h6">Period performance</Typography>
          <Typography variant="body2" color="text.secondary">The metrics and chart below use your selected date range.</Typography>
        </Box>
        <PeriodFilter period={period} onChange={setPeriod} />
        <ReportState report={widgets} label="Metrics" />
        {dates && <MetricGrid sx={metricGridStyle}>
          <DashboardMetric label="Impressions" value={widgets.data?.impressions} />
          <DashboardMetric label="Claims" value={widgets.data?.claims} />
          <DashboardMetric label="Spend" value={money(widgets.data?.spend)} />
          <DashboardMetric label="Total clicks" value={widgets.data?.totalClicks} />
          <DashboardMetric label="Clicks - Homepage" value={widgets.data?.homepage} />
          <DashboardMetric label="Clicks - Favorites" value={widgets.data?.favoriteClicks} />
          <DashboardMetric label="Clicks - Phone" value={widgets.data?.phoneClicks} />
          <DashboardMetric label="Clicks - Directions" value={widgets.data?.directionsClicks} />
          <DashboardMetric label="Clicks - T&C" value={widgets.data?.termsAndConditionsClicks} />
        </MetricGrid>}
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2} p={2.5} borderBottom="1px solid" borderColor="divider">
            <Box><Typography fontWeight={600}>{label}</Typography><Typography variant="body2" color="text.secondary" mt={0.5}>{description}</Typography></Box>
            <TextField select size="small" label="Metric" value={chartType} onChange={event => setChartType(event.target.value)} sx={{ minWidth: 190 }}>{Object.entries(charts).map(([value, [name]]) => <MenuItem key={value} value={value}>{name}</MenuItem>)}</TextField>
          </Stack>
          <ReportState report={chart} label="Chart" />
          <Box p={{ xs: 1.5, sm: 2.5 }}>{!dates ? <Typography color="text.secondary">Choose a valid period to view the chart.</Typography> : !chart.loading && !chart.error && chart.data && <Chart config={chart.data.config} points={chart.data.points} title={label} currency={['SPEND', 'BUDGET'].includes(chartType)} />}</Box>
        </Paper>
      </Stack>
    </Box>
  </Stack></Container></BusinessPage>
}
